import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';
import type { PaginatedRequest, PaginatedResponse } from '@app/api/types';

export type ProjectSummary = {
    projectId: string;
    projectName: string;
    unreadComments: number;
    isAdmin: boolean;
};

type ProjectsSummaryResponse = {
    data: ProjectSummary[];
};

type GetMentionSuggestionsPayload = {
    term: string;
    projectId: string;
};

type GetTranslationLockStatusPayload = {
    projectId: string;
    translationIds: string[];
};

type TranslationLockStatusType = {
    translationId: string;
    lockByUser: {
        id: string;
        fullName: string;
        email: string;
    };
};
type MentionCommonData = { name: string; gravatarHash: string };

type UserMention = { userId: number };
type GroupMention = { groupId: number };
type InviteMention = { email: string };

export type MentionType = (UserMention | GroupMention | InviteMention) & MentionCommonData;

export type AttachPointType = 'keys' | 'projects' | 'translations';

type ResourcePath = {
    projectId: string;
    attachPointType?: AttachPointType;
    attachPointId?: string;
    commentId?: string;
    replyCommentId?: string;
};

type CreateCommentPayload = ResourcePath & {
    message: string;
    mentions?: Array<UserMention | GroupMention | InviteMention>;
};

export type SuggestionType = { value: string; uid: string; userId: number; gravatarHash: string };

type GetCommentsResponse = {
    data: CommentData[];
};

export type CommentData = {
    id: string;
    projectId: string;
    author: {
        userId: number;
        name: string;
        gravatarHash: string;
    };
    message: string;
    attachPointType: AttachPointType;
    attachPointId: string;
    attachPointName: string;
    createdAt: number;
    modifiedAt: number;
    mentions: MentionType[];
    read: boolean;
    readAt: number;
    resolved: boolean;
    hasUnreadReplies: boolean;
    totalReplies?: number;
};

// Currently only comments in listing APIs have these extra fields
export type ListingCommentData = CommentData & {
    masterProjectId?: string;
    branchName?: string;
};

const collaborationApi = createApi({
    reducerPath: 'collaboration',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['Comment'],
    endpoints: (builder) => ({
        getMentionSuggestions: builder.mutation<SuggestionType[], GetMentionSuggestionsPayload>({
            query: ({ projectId, ...body }) => ({
                method: 'POST',
                url: `/collaboration/projects/${projectId}/mentions/project-users`,
                body,
            }),
        }),
        getProjectsSummary: builder.query<ProjectsSummaryResponse, { teamId: string }>({
            query: ({ teamId }) => ({
                method: 'GET',
                url: `/collaboration/teams/${teamId}/projects`,
            }),
        }),
        getProjectComments: builder.query<
            PaginatedResponse<ListingCommentData[]>,
            PaginatedRequest<{ projectId: string; showResolvedComments: boolean }>
        >({
            query: ({ projectId, size, after, before, at, showResolvedComments }) => ({
                url: `/collaboration/projects/${projectId}/comments`,
                params: {
                    'page[size]': size,
                    'page[after]': after,
                    'page[before]': before,
                    'page[at]': at,
                    'filter-resolved': Number(showResolvedComments),
                },
            }),
            providesTags: (comments: PaginatedResponse<CommentData[]> | undefined) =>
                comments
                    ? [
                          ...comments.data.map(({ id }) => ({ type: 'Comment' as const, id })),
                          { type: 'Comment', id: 'LIST' },
                      ]
                    : [{ type: 'Comment' as const, id: 'LIST' }],
        }),
        getKeyComments: builder.query<ListingCommentData[], { projectId: string; keyId: string }>({
            query: ({ projectId, keyId }) => ({
                url: `/collaboration/projects/${projectId}/keys/${keyId}/comments`,
            }),
            transformResponse: ({ data }: GetCommentsResponse) => data,
            providesTags: (comments: CommentData[] | undefined) =>
                comments
                    ? [...comments.map(({ id }) => ({ type: 'Comment' as const, id })), { type: 'Comment', id: 'LIST' }]
                    : [{ type: 'Comment' as const, id: 'LIST' }],
        }),
        getTranslationComments: builder.query<ListingCommentData[], { projectId: string; translationId: string }>({
            query: ({ projectId, translationId }) => ({
                url: `/collaboration/projects/${projectId}/translations/${translationId}/comments`,
            }),
            transformResponse: ({ data }: GetCommentsResponse) => data,
            providesTags: (comments: CommentData[] | undefined) =>
                comments
                    ? [...comments.map(({ id }) => ({ type: 'Comment' as const, id })), { type: 'Comment', id: 'LIST' }]
                    : [{ type: 'Comment' as const, id: 'LIST' }],
        }),
        getThreadComments: builder.query<
            ListingCommentData[],
            { projectId: string; attachPointType: AttachPointType; keyId: string; commentId: string }
        >({
            query: ({ projectId, attachPointType, keyId, commentId }) => ({
                url: `/collaboration/projects/${projectId}/${attachPointType}/${keyId}/comments/${commentId}/replies`,
            }),
            transformResponse: ({ data }: GetCommentsResponse) => data,
            providesTags: (comments: CommentData[] | undefined) =>
                comments
                    ? [...comments.map(({ id }) => ({ type: 'Comment' as const, id })), { type: 'Comment', id: 'LIST' }]
                    : [{ type: 'Comment' as const, id: 'LIST' }],
        }),
        postComment: builder.mutation<CommentData, CreateCommentPayload>({
            query: ({ projectId, attachPointType, attachPointId, ...body }) => ({
                method: 'POST',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments`,
                body,
            }),
            invalidatesTags: [{ type: 'Comment', id: 'LIST' }],
        }),
        postReply: builder.mutation<CommentData, CreateCommentPayload>({
            query: ({ projectId, attachPointType, attachPointId, commentId, ...body }) => ({
                method: 'POST',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments/${commentId}/replies`,
                body,
            }),
            invalidatesTags: [{ type: 'Comment', id: 'LIST' }],
        }),
        deleteReply: builder.mutation<void, ResourcePath>({
            query: ({ projectId, attachPointType, attachPointId, commentId, replyCommentId, ...body }) => ({
                method: 'DELETE',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments/${commentId}/replies/${replyCommentId}`,
                body,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Comment', id: arg.commentId }],
        }),
        editComment: builder.mutation<CommentData, CreateCommentPayload>({
            query: ({ projectId, attachPointType, attachPointId, commentId, ...body }) => ({
                method: 'PUT',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments/${commentId}`,
                body,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Comment', id: arg.commentId },
                { type: 'Comment', id: 'LIST' },
            ],
        }),
        deleteComment: builder.mutation<void, ResourcePath & { invalidateCommentId: boolean }>({
            query: ({ projectId, attachPointType, attachPointId, commentId, ...body }) => ({
                method: 'DELETE',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments/${commentId}`,
                body,
            }),
            invalidatesTags: (result, error, { invalidateCommentId, commentId }) =>
                invalidateCommentId ? [{ type: 'Comment', id: commentId }] : [],
        }),
        markCommentsAsRead: builder.mutation<void, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'PUT',
                url: `/collaboration/projects/${projectId}/comments`,
                body: { read: true },
            }),
            invalidatesTags: [{ type: 'Comment', id: 'LIST' }],
        }),
        resolveComment: builder.mutation<void, ResourcePath>({
            query: ({ projectId, attachPointType, attachPointId, commentId }) => ({
                method: 'PATCH',
                url: `/collaboration/projects/${projectId}/${attachPointType}/${attachPointId}/comments/${commentId}`,
                body: { resolved: true },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Comment', id: arg.commentId },
                { type: 'Comment', id: 'LIST' },
            ],
        }),
        getTranslationLockStatus: builder.query<TranslationLockStatusType[], GetTranslationLockStatusPayload>({
            query: ({ projectId, ...body }) => ({
                method: 'POST',
                url: `/project/${projectId}/translations/actions/get-locked`,
                body,
            }),
            transformResponse: ({ data }) => data,
        }),
    }),
});

export const {
    useGetProjectsSummaryQuery,
    useLazyGetProjectCommentsQuery,
    useGetProjectCommentsQuery,
    useGetKeyCommentsQuery,
    useLazyGetKeyCommentsQuery,
    useGetTranslationCommentsQuery,
    useGetMentionSuggestionsMutation,
    usePostCommentMutation,
    usePostReplyMutation,
    useDeleteReplyMutation,
    useEditCommentMutation,
    useDeleteCommentMutation,
    useMarkCommentsAsReadMutation,
    useResolveCommentMutation,
    useGetThreadCommentsQuery,
    useGetTranslationLockStatusQuery,
} = collaborationApi;

export default collaborationApi;
